import axios from 'axios';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { defaultNS, NAMESPACES, resources } from './resources';
import { DEFAULT_LANGUAGE, intlLocale, isSupportedLanguage, LanguageCode } from './languages';

export const LANGUAGE_STORAGE_KEY = 'language';

const stored = (): string | null => {
    try {
        return localStorage.getItem(LANGUAGE_STORAGE_KEY);
    } catch {
        return null;
    }
};

/** The saved choice on this device, else the browser's language when we offer it, else English. */
export const initialLanguage = (): LanguageCode => {
    const saved = stored();
    if (isSupportedLanguage(saved)) return saved;
    const browser = (typeof navigator !== 'undefined' ? navigator.language : '').split('-')[0];
    return isSupportedLanguage(browser) ? browser : DEFAULT_LANGUAGE;
};

i18n.use(initReactI18next).init({
    resources,
    lng: initialLanguage(),
    fallbackLng: DEFAULT_LANGUAGE,
    ns: [...NAMESPACES],
    defaultNS,
    interpolation: { escapeValue: false }, // React escapes already
    returnNull: false,
});

// The page's language, and the language the API answers errors in.
const syncDocument = (lng: string) => {
    if (typeof document !== 'undefined') document.documentElement.lang = lng;
    axios.defaults.headers.common['Accept-Language'] = lng;
};
syncDocument(i18n.language);

i18n.on('languageChanged', lng => {
    syncDocument(lng);
    try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
    } catch {
        // Private mode or blocked storage: the choice lasts for this page only.
    }
});

/** Formats a date (Date or ISO string) in the current language, with Latin digits. */
export const formatDate = (value: Date | string, options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }) =>
    new Intl.DateTimeFormat(intlLocale(i18n.language), options)
        .format(typeof value === 'string' ? new Date(value) : value);

export default i18n;
